/**
 * Parashari divisional (varga) sign from D1 sign + degree in sign.
 * Formulas align with standard BPHS / JHora-style teaching (movable-fixed-dual, odd-even rules).
 */
import { LAGNA_RASHI_OPTIONS } from '../data/jyotishaUcchNeechCalculator';

const MOVABLE = new Set([1, 4, 7, 10]);
const FIXED = new Set([2, 5, 8, 11]);
const DUAL = new Set([3, 6, 9, 12]);

/** @param {number} sign1 1–12 @param {number} degree 0–29.999 */
export function clampSignDegree(sign1, degree) {
  const sign = Math.min(12, Math.max(1, Math.floor(Number(sign1)) || 1));
  const d = Math.max(0, Math.min(29.999999, Number(degree) || 0));
  return { sign, degree: d };
}

/** 1-based part index within sign (1 … divisions). */
export function vargaPartIndex(degreeInSign, divisions) {
  const span = 30 / divisions;
  return Math.min(divisions, Math.floor(degreeInSign / span) + 1);
}

function partSpanDegrees(divisions) {
  const span = 30 / divisions;
  const whole = Math.floor(span);
  const minutes = Math.round((span - whole) * 60);
  if (minutes === 0) return `${whole}°`;
  if (whole === 0) return `${minutes}′`;
  return `${whole}°${String(minutes).padStart(2, '0')}′`;
}

/** @param {number} sign1 @param {number} part @param {'movable-self' | 'fixed-ninth' | 'dual-fifth' | 'odd-self' | 'even-ninth' | 'odd-leo' | 'even-cancer' | 'self' | 'odd-aries' | 'even-libra'} rule */
function vargaStartSign0(sign1, rule) {
  const s = sign1 - 1;
  switch (rule) {
    case 'movable-self':
      if (MOVABLE.has(sign1)) return s;
      if (FIXED.has(sign1)) return (s + 8) % 12;
      return (s + 4) % 12;
    case 'fixed-ninth':
      if (FIXED.has(sign1)) return (s + 8) % 12;
      if (MOVABLE.has(sign1)) return s;
      return (s + 4) % 12;
    case 'dual-fifth':
      if (DUAL.has(sign1)) return (s + 4) % 12;
      if (MOVABLE.has(sign1)) return s;
      return (s + 8) % 12;
    case 'odd-self':
      return sign1 % 2 === 1 ? s : (s + 8) % 12;
    case 'even-ninth':
      return sign1 % 2 === 0 ? (s + 8) % 12 : s;
    case 'odd-leo':
      return sign1 % 2 === 1 ? 4 : 3;
    case 'even-cancer':
      return sign1 % 2 === 0 ? 3 : 4;
    case 'self':
      return s;
    case 'odd-aries':
      return sign1 % 2 === 1 ? 0 : 6;
    case 'even-libra':
      return sign1 % 2 === 0 ? 6 : 0;
    default:
      return s;
  }
}

/**
 * @param {number} sign1 D1 sign 1–12
 * @param {number} degree degree in sign
 * @param {number} divisions D-number parts per sign
 * @param {'movable-self' | 'fixed-ninth' | 'dual-fifth' | 'odd-self' | 'even-ninth' | 'odd-leo' | 'even-cancer' | 'self' | 'odd-aries' | 'even-libra'} startRule
 */
export function divisionalSign(sign1, degree, divisions, startRule) {
  const { sign, degree: d } = clampSignDegree(sign1, degree);
  const part = vargaPartIndex(d, divisions);
  const start0 = vargaStartSign0(sign, startRule);
  const result0 = (start0 + part - 1) % 12;
  return {
    d1Sign: sign,
    degree: d,
    divisions,
    part,
    partSpan: partSpanDegrees(divisions),
    vargaSign: result0 + 1,
  };
}

export function d1Sign(sign1, degree = 0) {
  const { sign, degree: d } = clampSignDegree(sign1, degree);
  return { d1Sign: sign, degree: d, divisions: 1, part: 1, partSpan: '30°', vargaSign: sign };
}

export function d9Navamsa(sign1, degree) {
  return divisionalSign(sign1, degree, 9, 'movable-self');
}

export function d10Dasamsa(sign1, degree) {
  return divisionalSign(sign1, degree, 10, 'odd-self');
}

export function d7Saptamsa(sign1, degree) {
  const { sign, degree: d } = clampSignDegree(sign1, degree);
  const part = vargaPartIndex(d, 7);
  const start0 = sign % 2 === 1 ? sign - 1 : (sign - 1 + 6) % 12;
  return {
    d1Sign: sign,
    degree: d,
    divisions: 7,
    part,
    partSpan: partSpanDegrees(7),
    vargaSign: (start0 + part - 1) % 12 + 1,
  };
}

export function d3Drekkana(sign1, degree) {
  const { sign, degree: d } = clampSignDegree(sign1, degree);
  const part = vargaPartIndex(d, 3);
  const offset = part === 1 ? 0 : part === 2 ? 4 : 8;
  const result0 = (sign - 1 + offset) % 12;
  return {
    d1Sign: sign,
    degree: d,
    divisions: 3,
    part,
    partSpan: '10°',
    vargaSign: result0 + 1,
  };
}

export function d12Dwadasamsa(sign1, degree) {
  return divisionalSign(sign1, degree, 12, 'self');
}

export function d24Siddhamsa(sign1, degree) {
  return divisionalSign(sign1, degree, 24, 'odd-leo');
}

export function d60Shashtiamsa(sign1, degree) {
  return divisionalSign(sign1, degree, 60, 'odd-aries');
}

/** @param {number} signNum 1–12 */
export function rashiLabel(signNum, lang = 'en') {
  const row = LAGNA_RASHI_OPTIONS.find((r) => r.value === signNum);
  if (!row) return '-';
  return lang === 'hi' ? row.hi : row.en;
}

/** Whole-sign house of planet sign when lagna sign is known. */
export function houseFromSign(lagnaSign, planetSign) {
  const lagna = ((Number(lagnaSign) - 1 + 12) % 12) + 1;
  const planet = ((Number(planetSign) - 1 + 12) % 12) + 1;
  return ((planet - lagna + 12) % 12) + 1;
}

export const INTRO_VARGA_LESSON_STEPS = [
  {
    step: 1,
    code: 'D1',
    sanskrit: 'Rashi',
    divisions: 1,
    partSpanEn: '30° per sign',
    partSpanHi: 'प्रति राशि 30°',
    useEn: 'Birth chart · whole life map',
    useHi: 'जन्म कुंडली · पूरी ज़िंदगी का नक्शा',
    example: { planet: 'Mars', sign: 2, degree: 18 },
    calc: d1Sign,
  },
  {
    step: 2,
    code: 'D9',
    sanskrit: 'Navamsa',
    divisions: 9,
    partSpanEn: '3°20′ each',
    partSpanHi: 'प्रत्येक 3°20′',
    useEn: 'Marriage, dharma, inner strength',
    useHi: 'विवाह, धर्म, भीतरी शक्ति',
    example: { planet: 'Mars', sign: 2, degree: 18 },
    calc: d9Navamsa,
  },
  {
    step: 3,
    code: 'D10',
    sanskrit: 'Dasamsa',
    divisions: 10,
    partSpanEn: '3° each',
    partSpanHi: 'प्रत्येक 3°',
    useEn: 'Career, rank, public duty',
    useHi: 'कर्म, पद, सार्वजनिक जिम्मेदारी',
    example: { planet: 'Mars', sign: 2, degree: 18 },
    calc: d10Dasamsa,
  },
  {
    step: 4,
    code: 'D7',
    sanskrit: 'Saptamsa',
    divisions: 7,
    partSpanEn: '≈4°17′ each',
    partSpanHi: '≈4°17′ प्रत्येक',
    useEn: 'Children, creative legacy',
    useHi: 'संतान, रचनात्मक विरासत',
    example: { planet: 'Mars', sign: 2, degree: 18 },
    calc: d7Saptamsa,
  },
  {
    step: 5,
    code: 'D12',
    sanskrit: 'Dwadasamsa',
    divisions: 12,
    partSpanEn: '2°30′ each',
    partSpanHi: 'प्रत्येक 2°30′',
    useEn: 'Parents and ancestry',
    useHi: 'माता-पिता और वंश',
    example: { planet: 'Mars', sign: 2, degree: 18 },
    calc: d12Dwadasamsa,
  },
  {
    step: 6,
    code: 'D24',
    sanskrit: 'Siddhamsa',
    divisions: 24,
    partSpanEn: '1°15′ each',
    partSpanHi: 'प्रत्येक 1°15′',
    useEn: 'Education and learning',
    useHi: 'शिक्षा और विद्या',
    example: { planet: 'Mars', sign: 2, degree: 18 },
    calc: d24Siddhamsa,
  },
  {
    step: 7,
    code: 'D60',
    sanskrit: 'Shashtiamsa',
    divisions: 60,
    partSpanEn: '0°30′ each',
    partSpanHi: 'प्रत्येक 0°30′',
    useEn: 'Finest karmic summary (needs accurate birth time)',
    useHi: 'सूक्ष्मतम कर्म सार (सटीक जन्म समय चाहिए)',
    example: { planet: 'Mars', sign: 2, degree: 18 },
    calc: d60Shashtiamsa,
  },
];
